import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import * as moment from 'moment-timezone';
import { StagingService } from '../../component/assigned-trips/staging/staging.service';
import { SharedDataService } from '../../service/shared-service/shared-data.service';
import { sweetAlert } from '../../utils/swal';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-staging-trip-detail-modal',
  templateUrl: './staging-trip-detail-modal.component.html',
  styleUrls: ['./staging-trip-detail-modal.component.css']
})
export class StagingTripDetailModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() trip: any;
  @Input() prePayorsList: any;
  @Input() preSpaceTypes: any;
  @Input() preCooperates: any;
  @Input() preLevelOfServices: any;
  @Input() isUnconfirmMode: boolean = false;
  @Output() onCloseModal = new EventEmitter<any>();
  @Output() onTripUpdated = new EventEmitter<any>();

  user: any;
  timeZone: string = 'America/New_York';
  isLoading: boolean = false;
  activeTab: string = 'overview'; // 'overview' | 'route' | 'member' | 'billing' | 'notes' | 'actions' | 'logs'
  requestLogs: any[] = [];
  isLoadingLogs: boolean = false;

  // Action states
  showUnconfirmForm: boolean = false;
  unconfirmReason: string = '';
  unconfirmError: string = '';

  showSuggestTimeForm: boolean = false;
  suggestedTimeVal: string = '';
  suggestTimeError: string = '';

  quickReasons: string[] = [
    'No vehicle available',
    'Driver unavailable in this area',
    'Schedule conflict / Outside operating hours',
    'Special equipment not available (Bariatric/Stretcher)',
    'Member requested cancellation',
    'Duplicate request'
  ];

  isLoadingDetails: boolean = false;
  showMap: boolean = false;
  private mapTimer: any;

  // Stable Map coordinates & marker config
  originLat: number = 0;
  originLng: number = 0;
  destinationLat: number = 0;
  destinationLng: number = 0;
  hasValidMapCoordinates: boolean = false;

  readonly originMarkerLabel = { color: 'white', fontSize: '11px', fontWeight: 'bold', text: 'P' };
  readonly destMarkerLabel = { color: 'white', fontSize: '11px', fontWeight: 'bold', text: 'D' };
  readonly originMarkerIcon = {
    url: 'assets/images/map/bucket_pin2.png',
    scaledSize: { width: 34, height: 34 },
    labelOrigin: { x: 17, y: 12 }
  };
  readonly destMarkerIcon = {
    url: 'assets/images/map/bucket_pin3.png',
    scaledSize: { width: 34, height: 34 },
    labelOrigin: { x: 17, y: 12 }
  };

  // Standing order cached state
  standingOrder: boolean = false;
  selectedDays: { [key: number]: boolean } = {};
  recurringEndDateFormatted: string = '';

  readonly weekDays = [
    { label: 'Sun', day: 0 },
    { label: 'Mon', day: 1 },
    { label: 'Tue', day: 2 },
    { label: 'Wed', day: 3 },
    { label: 'Thu', day: 4 },
    { label: 'Fri', day: 5 },
    { label: 'Sat', day: 6 }
  ];

  constructor(
    private stagingService: StagingService,
    private sharedDataService: SharedDataService,
    private toastr: ToastrService
  ) { }

  ngOnInit() {
    this.getUser();
    if (this.trip) {
      this.normalizeTripData();
      this.computeDerivedState();
      this.syncState();
      this.loadTripDetails();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.trip && !changes.trip.firstChange && this.trip) {
      this.normalizeTripData();
      this.computeDerivedState();
      this.syncState();
      this.loadTripDetails();
    }
  }

  computeDerivedState() {
    if (!this.trip) {
      this.originLat = 0;
      this.originLng = 0;
      this.destinationLat = 0;
      this.destinationLng = 0;
      this.hasValidMapCoordinates = false;
      this.standingOrder = false;
      this.selectedDays = {};
      this.recurringEndDateFormatted = '';
      return;
    }

    const t = this.trip;
    this.originLat = Number(t.jobOriginLatitude || t.pickupLatitude || (t.pickupLocation && t.pickupLocation.latitude) || (t.origin && t.origin.lat) || 0);
    this.originLng = Number(t.jobOriginLongitude || t.pickupLongitude || (t.pickupLocation && t.pickupLocation.longitude) || (t.origin && t.origin.lng) || 0);
    this.destinationLat = Number(t.jobDestinationLatitude || t.dropoffLatitude || (t.dropoffLocation && t.dropoffLocation.latitude) || (t.destination && t.destination.lat) || 0);
    this.destinationLng = Number(t.jobDestinationLongitude || t.dropoffLongitude || (t.dropoffLocation && t.dropoffLocation.longitude) || (t.destination && t.destination.lng) || 0);

    this.hasValidMapCoordinates = !!(this.originLat && this.originLng && this.destinationLat && this.destinationLng);

    // Standing order computation
    this.standingOrder = t.status === 'standing' || !!t.isRecurring || !!t.isStandingOrder || !!(t.recurringDays && t.recurringDays.length);
    const selDays: { [key: number]: boolean } = {};
    if (t.recurringDays && Array.isArray(t.recurringDays)) {
      t.recurringDays.forEach((d: any) => {
        if (!d && d !== 0) return;
        if (typeof d === 'number') {
          selDays[d] = true;
        } else if (d.checked !== false && d.day !== undefined && d.day !== null) {
          selDays[Number(d.day)] = true;
        }
      });
    }
    this.selectedDays = selDays;

    const end = t.recurringEndDate || t.preRecurringEndDate;
    this.recurringEndDateFormatted = end ? this.formatDate(end) : '';
  }

  initMapDeferred() {
    this.showMap = false;
    if (this.mapTimer) clearTimeout(this.mapTimer);

    if (!this.hasValidMapCoordinates) return;

    this.mapTimer = setTimeout(() => {
      this.showMap = true;
    }, 300);
  }

  loadTripDetails() {
    const stagingId = this.trip ? (this.trip._id || this.trip.id || this.trip.memberAssign || this.trip.tripId) : null;
    if (!stagingId) {
      this.initMapDeferred();
      return;
    }

    this.isLoadingDetails = true;
    this.stagingService.getMemberLinkRequest(stagingId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isLoadingDetails = false;
          if (res) {
            this.trip = Object.assign({}, this.trip, res);
            this.normalizeTripData();
            this.computeDerivedState();
            this.syncState();
          }
          this.initMapDeferred();
        },
        (err) => {
          this.isLoadingDetails = false;
          console.error('Error fetching staging trip details:', err);
          this.initMapDeferred();
        }
      );
  }

  normalizeTripData() {
    if (!this.trip) return;
    const t = this.trip;
    if (!t.jobOriginAddress && (t.pickupAddress || (t.pickupLocation && t.pickupLocation.address))) {
      t.jobOriginAddress = t.pickupAddress || (t.pickupLocation && t.pickupLocation.address);
    }
    if (!t.jobDestinationAddress && (t.dropoffAddress || (t.dropoffLocation && t.dropoffLocation.address))) {
      t.jobDestinationAddress = t.dropoffAddress || (t.dropoffLocation && t.dropoffLocation.address);
    }
    if (!t.scheduleTime && t.pickupDateTime) {
      t.scheduleTime = t.pickupDateTime;
    }
    if (!t.appointmentTime && t.dropoffDateTime) {
      t.appointmentTime = t.dropoffDateTime;
    }
  }

  syncState() {
    if (!this.trip) return;
    if (this.trip.intakeSuggestedTime) {
      this.suggestedTimeVal = this.trip.intakeSuggestedTime;
    } else if (this.trip.scheduleTime) {
      this.suggestedTimeVal = this.trip.scheduleTime;
    }
    if (this.trip.intakeRejectionReason || this.trip.intakeRejectReason) {
      this.unconfirmReason = this.trip.intakeRejectionReason || this.trip.intakeRejectReason;
    }
    this.fetchLogs();
  }

  fetchLogs() {
    if (!this.trip) return;
    const memberAssignId = this.trip._id || this.trip.id || this.trip.memberAssign;
    this.isLoadingLogs = true;
    this.stagingService.getRequestLogs(memberAssignId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isLoadingLogs = false;
          if (Array.isArray(data)) {
            this.requestLogs = data.sort((a, b) => {
              const tA = new Date(a.createdAt || a.created_at || a.timestamp || 0).getTime();
              const tB = new Date(b.createdAt || b.created_at || b.timestamp || 0).getTime();
              return tB - tA;
            });
          } else {
            this.requestLogs = [];
          }
        },
        () => {
          this.isLoadingLogs = false;
        }
      );
  }

  breakString(str: string): string {
    if (!str) return '';
    return str.replace(/\n/g, '<br>');
  }

  isObjectId(val: any): boolean {
    if (!val || typeof val !== 'string') return false;
    return /^[0-9a-fA-F]{24}$/.test(val);
  }

  getSpaceTypeName(): string {
    if (!this.trip) return '';
    const st = this.trip.spaceType;
    if (st && typeof st === 'object' && st.title) return st.title;
    if (st && this.preSpaceTypes && this.preSpaceTypes[st] && this.preSpaceTypes[st].title) {
      return this.preSpaceTypes[st].title;
    }
    const los = this.trip.los;
    if (los && typeof los === 'object' && (los.title || los.name)) return los.title || los.name;
    if (los && this.preLevelOfServices && this.preLevelOfServices[los]) {
      return this.preLevelOfServices[los].title || this.preLevelOfServices[los].name || '';
    }
    if (st && !this.isObjectId(st)) return st;
    if (los && !this.isObjectId(los)) return los;
    return '';
  }

  getFundingSourceName(): string {
    if (!this.trip) return '';
    const coop = this.trip.cooperate;
    if (coop && typeof coop === 'object' && (coop.displayName || coop.name || coop.companyName)) {
      return coop.displayName || coop.name || coop.companyName;
    }
    if (coop && this.preCooperates && this.preCooperates[coop] && this.preCooperates[coop].displayName) {
      return this.preCooperates[coop].displayName;
    }
    if (this.trip.corporate && (this.trip.corporate.displayName || this.trip.corporate.name)) {
      return this.trip.corporate.displayName || this.trip.corporate.name;
    }
    if (coop && !this.isObjectId(coop)) return coop;
    return '';
  }

  // Member id used by GET memberlink/requests/trips/count
  getClientId(): string {
    if (!this.trip) return '';
    const client = this.trip.client;
    if (client && typeof client === 'object') return client._id || '';
    return client || '';
  }

  // --- Fields from the New Trip form -------------------------------------------

  getLevelOfServiceName(): string {
    if (!this.trip) return '';
    const los = this.trip.los;
    if (los && typeof los === 'object') return los.title || los.name || '';
    if (los && this.preLevelOfServices && this.preLevelOfServices[los]) {
      return this.preLevelOfServices[los].title || this.preLevelOfServices[los].name || '';
    }
    return '';
  }

  // Same note fields (and order) the staging trip card reads
  getNotes(): string {
    if (!this.trip) return '';
    const t = this.trip;
    return t.driverNotes || t.driverNote || t.note || t.readyNotes || t.companyNote || t.billerNote || '';
  }

  isStandingOrder(): boolean {
    return this.standingOrder;
  }

  hasSuggestedPlan(): boolean {
    if (!this.trip) return false;
    return !!(this.trip.suggested || this.trip.intakeSuggestedTime || this.trip.intakeStatus === 'suggested_plan');
  }

  getSuggestedSpaceTypeName(): string {
    if (!this.trip || !this.trip.suggested) return '';
    const st = this.trip.suggested.spaceType;
    if (st && typeof st === 'object' && st.title) return st.title;
    if (st && this.preSpaceTypes && this.preSpaceTypes[st] && this.preSpaceTypes[st].title) {
      return this.preSpaceTypes[st].title;
    }
    const los = this.trip.suggested.los;
    if (los && typeof los === 'object' && (los.title || los.name)) return los.title || los.name;
    if (los && this.preLevelOfServices && this.preLevelOfServices[los]) {
      return this.preLevelOfServices[los].title || this.preLevelOfServices[los].name || '';
    }
    if (st && !this.isObjectId(st)) return st;
    if (los && !this.isObjectId(los)) return los;
    return '';
  }

  getSuggestedLevelOfServiceName(): string {
    if (!this.trip || !this.trip.suggested) return '';
    const los = this.trip.suggested.los;
    if (los && typeof los === 'object') return los.title || los.name || '';
    if (los && this.preLevelOfServices && this.preLevelOfServices[los]) {
      return this.preLevelOfServices[los].title || this.preLevelOfServices[los].name || '';
    }
    if (los && !this.isObjectId(los)) return los;
    return '';
  }

  isDaySelected(day: number): boolean {
    return !!this.selectedDays[day];
  }

  isSuggestedDaySelected(day: number): boolean {
    if (!this.trip || !this.trip.suggested || !this.trip.suggested.recurringDays) return false;
    const days = (this.trip.suggested.recurringDays || [])
      .map((d: any) => (typeof d === 'number' ? d : Number(d.day)));
    return days.includes(day);
  }

  getRecurringEndDate(): string {
    return this.recurringEndDateFormatted;
  }

  getPayorName(payorId?: any): string {
    let pid = payorId;
    if (!pid && this.trip) {
      pid = this.trip.payorId || this.trip.payor;
    }
    if (!pid) return '';
    if (typeof pid === 'object' && (pid.name || pid.displayName)) {
      return pid.name || pid.displayName;
    }
    if (this.prePayorsList && this.prePayorsList[pid]) {
      return this.prePayorsList[pid].name || this.prePayorsList[pid].displayName || '';
    }
    if (this.trip && this.trip.payorName) return this.trip.payorName;
    if (typeof pid === 'string' && !this.isObjectId(pid)) return pid;
    return '';
  }

  getClientDisplayId(): string {
    if (!this.trip) return '';
    const pc = this.trip.priorityClient;
    if (pc && pc.clientId && !this.isObjectId(pc.clientId)) return pc.clientId;
    if (pc && pc.clientCode && !this.isObjectId(pc.clientCode)) return pc.clientCode;
    if (this.trip.clientCode && !this.isObjectId(this.trip.clientCode)) return this.trip.clientCode;
    if (this.trip.clientId && !this.isObjectId(this.trip.clientId)) return this.trip.clientId;
    return '';
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(user => {
        if (user) {
          this.user = user;
          if (user.company && user.company.timeZone) {
            this.timeZone = user.company.timeZone;
          } else if (user.timeZone) {
            this.timeZone = user.timeZone;
          }
        }
      });
  }

  closeModal() {
    this.onCloseModal.emit(this.trip);
  }

  setTab(tab: string) {
    this.activeTab = tab;
  }

  formatDateTime(dateStr: string): string {
    if (!dateStr) return 'N/A';
    return moment(dateStr).tz(this.timeZone).format('MM/DD/YYYY hh:mm A');
  }

  formatTime(dateStr: string): string {
    if (!dateStr) return 'N/A';
    return moment(dateStr).tz(this.timeZone).format('hh:mm A');
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    return moment(dateStr).tz(this.timeZone).format('MM/DD/YYYY');
  }

  selectQuickReason(reason: string) {
    this.unconfirmReason = reason;
    this.unconfirmError = '';
  }

  confirmIntake() {
    if (!this.trip) return;
    const tripId = this.trip._id || this.trip.id;
    this.isLoading = true;

    this.stagingService.confirmTrip(tripId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isLoading = false;
          this.trip.intakeStatus = 'confirmed';
          if (data && data.intakeStatus) {
            this.trip.intakeStatus = data.intakeStatus;
          }
          this.toastr.success('Trip confirmed successfully', 'Confirmed');
          this.onTripUpdated.emit({ trip: this.trip, action: 'confirmed' });
        },
        (err) => {
          this.isLoading = false;
          const msg = (err && err.message) ? err.message : (err || 'Failed to confirm trip');
          sweetAlert('Error', msg, 'error', 'OK');
        }
      );
  }

  submitUnconfirm() {
    if (!this.unconfirmReason || !this.unconfirmReason.trim()) {
      this.unconfirmError = 'Please provide or select a rejection reason.';
      return;
    }
    const reason = this.unconfirmReason.trim();
    const tripId = this.trip._id || this.trip.id;
    this.isLoading = true;

    this.stagingService.unconfirmTrip(tripId, reason)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isLoading = false;
          this.trip.intakeStatus = 'unconfirmed';
          this.trip.intakeRejectionReason = reason;
          if (data) {
            if (data.intakeStatus) this.trip.intakeStatus = data.intakeStatus;
            if (data.intakeRejectionReason) this.trip.intakeRejectionReason = data.intakeRejectionReason;
          }
          this.showUnconfirmForm = false;
          this.toastr.warning('Trip marked as not authorized', 'Not Authorized');
          this.onTripUpdated.emit({ trip: this.trip, action: 'unconfirmed' });
          if (this.isUnconfirmMode) {
            this.closeModal();
          }
        },
        (err) => {
          this.isLoading = false;
          const msg = (err && err.message) ? err.message : (err || 'Failed to unconfirm trip');
          sweetAlert('Error', msg, 'error', 'OK');
        }
      );
  }

  submitSuggestTime() {
    if (!this.suggestedTimeVal) {
      this.suggestTimeError = 'Please specify a suggested time.';
      return;
    }
    const tripId = this.trip._id || this.trip.id;
    this.isLoading = true;

    this.stagingService.suggestTime(tripId, this.suggestedTimeVal)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isLoading = false;
          this.trip.intakeStatus = 'suggested_time';
          this.trip.intakeSuggestedTime = this.suggestedTimeVal;
          if (data) {
            if (data.intakeStatus) this.trip.intakeStatus = data.intakeStatus;
            if (data.intakeSuggestedTime) this.trip.intakeSuggestedTime = data.intakeSuggestedTime;
          }
          this.showSuggestTimeForm = false;
          this.toastr.info('New pickup/dropoff time suggested', 'Suggested Time');
          this.onTripUpdated.emit({ trip: this.trip, action: 'suggested_time' });
        },
        (err) => {
          this.isLoading = false;
          const msg = (err && err.message) ? err.message : (err || 'Failed to suggest time');
          sweetAlert('Error', msg, 'error', 'OK');
        }
      );
  }

  showLogsModal: boolean = false;

  openLogsModal() {
    this.showLogsModal = true;
  }

  closeLogsModal() {
    this.showLogsModal = false;
  }

  ngOnDestroy(): void {
    if (this.mapTimer) {
      clearTimeout(this.mapTimer);
    }
  }
}
